import { Brain } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';
import { useLabeling } from '../../context/useLabeling';
import { IMAGE_LABELS } from '../../types/LabelingTypes';
import type { ImageLabel } from '../../types/LabelingTypes';
import { LABEL_FILL, LABEL_TEXT } from '../../utils/labelStateClasses';

export function BulkBar() {
  const { selected, bulkLabel, bulkClassifyWithAi, clearSelection } = useLabeling();
  const { t } = useTranslation();

  const stateLabelMap: Record<ImageLabel, string> = {
    ok: t('labeling.state.ok'),
    worn: t('labeling.state.worn'),
    unreadable: t('labeling.state.unreadable'),
  };

  if (selected.size === 0) return null;

  return (
    <div className="sticky top-2 z-10 flex items-center gap-3 rounded-2xl border border-border bg-card shadow-lg px-4 py-2 flex-wrap">
      {/* Count badge */}
      <Badge>{t('labeling.grid.selectedCount', { count: selected.size })}</Badge>

      {/* Mark as label */}
      <span className="text-sm text-muted-foreground">{t('labeling.grid.markAs')}</span>

      {/* Compact state buttons: the state colour is the meaning */}
      {IMAGE_LABELS.map((state) => (
        <Button
          key={state}
          variant="outline"
          size="sm"
          onClick={() => bulkLabel(state)}
          className={LABEL_TEXT[state]}
        >
          <span className={cn('w-2 h-2 rounded-full shrink-0', LABEL_FILL[state])} aria-hidden />
          {stateLabelMap[state]}
        </Button>
      ))}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Classify with AI */}
      <Button variant="outline" size="sm" onClick={() => void bulkClassifyWithAi()}>
        <Brain aria-hidden />
        {t('labeling.grid.bulkClassify')}
      </Button>

      {/* Deselect */}
      <Button variant="ghost" size="sm" onClick={() => clearSelection()}>
        {t('labeling.grid.deselect')}
      </Button>
    </div>
  );
}
